import { useState } from 'react';
import { Check, Plus } from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { Tag } from '@/components/ui/badges';
import { fmtDateShort, isoLocalDate } from '@/lib/domain/format';
import { useMe } from '@/features/identity/useMe';
import { useReferenceData } from '@/features/pipeline/useReferenceData';
import { useChangeTaskState, useCreateTask, useTasks } from './api';
import { TASK_STATE_LABELS, type ActivityLinkType, type TaskListItem, type TaskState } from './types';
import { isOverdue } from './tasksView';

const INPUT = 'rounded border border-vega-line bg-vega-surface px-2 py-1 text-[12.5px] text-vega-ink outline-none';
const STATE_TONE: Record<TaskState, 'default' | 'info' | 'accent'> = {
  Open: 'default',
  InProgress: 'info',
  Done: 'accent',
  Cancelled: 'default',
};
// Achado F7 (revisão 2026-07): avançar em UTC e formatar como UTC podia dar "amanhã" (ou "hoje")
// deslocado, dependendo do horário local — avança em dias-de-calendário locais.
const inDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return isoLocalDate(d);
};

/**
 * Seção "Tarefas vinculadas" da ficha (DealDrawer) — espelha o DealTarefasTab do mock e a
 * CommitteesSection: lista as tarefas do vínculo, cria inline (título/prazo/responsável) e conclui
 * pelo checkbox. É aqui que vive a criação de Tarefa (DEC-048: a tela Tarefas virou read/triage).
 */
export function TasksSection({ linkType, linkId }: { linkType: ActivityLinkType; linkId: string }) {
  const me = useMe(true);
  const { data: ref } = useReferenceData();
  const { data, isLoading } = useTasks({ linkType, linkId });
  const create = useCreateTask();
  const changeState = useChangeTaskState();

  const users = ref?.users ?? [];
  const userName = (id: string) => users.find((u) => u.id === id)?.name ?? '—';

  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState(inDays(7));
  const [assignee, setAssignee] = useState('');

  const tasks = data?.items ?? [];
  const now = new Date();

  const submit = async () => {
    if (!title.trim() || !assignee) return;
    await create.mutateAsync({
      title: title.trim(),
      assigneeUserId: assignee,
      dueDateUtc: dueDate ? new Date(dueDate + 'T12:00:00Z').toISOString() : null,
      linkType,
      linkId,
    });
    setTitle('');
    setDueDate(inDays(7));
    setAssignee(me.data?.userId ?? '');
    setCreating(false);
  };

  const openForm = () => {
    setAssignee(me.data?.userId ?? '');
    setCreating(true);
  };

  const toggle = (t: TaskListItem) =>
    changeState.mutate({ id: t.id, state: t.state === 'Done' ? 'Open' : 'Done' });

  return (
    <section className="mb-6">
      <div className="mb-2 flex items-center justify-between border-b border-vega-line pb-1.5">
        <h3 className="text-[10.5px] font-semibold uppercase tracking-wide text-vega-muted">
          Tarefas vinculadas <span className="font-normal text-vega-muted-2">({tasks.length})</span>
        </h3>
        <button
          type="button"
          onClick={openForm}
          className="flex items-center gap-1 text-[11px] text-vega-ink hover:underline"
        >
          <Plus size={13} /> Nova tarefa
        </button>
      </div>

      {creating && (
        <div className="mb-3 flex flex-col gap-2 rounded border border-vega-line-2 bg-vega-surface-2 p-3">
          <input
            autoFocus
            className={`${INPUT} w-full`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
              if (e.key === 'Escape') setCreating(false);
            }}
            placeholder="Título da tarefa…"
            maxLength={200}
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-vega-muted">Prazo</span>
            <input type="date" className={INPUT} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            <span className="ml-1 text-[11px] text-vega-muted">Responsável</span>
            <select className={INPUT} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">Selecione…</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
            <div className="flex-1" />
            <button type="button" onClick={() => setCreating(false)} className="text-[11px] text-vega-muted">
              Cancelar
            </button>
            <button
              type="button"
              disabled={!title.trim() || !assignee || create.isPending}
              onClick={() => void submit()}
              className="bg-vega-ink px-2.5 py-1 text-[11px] text-vega-surface disabled:opacity-40"
            >
              Criar
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-[12.5px] text-vega-muted-2">Carregando…</p>
      ) : tasks.length === 0 && !creating ? (
        <p className="py-2 text-[12px] text-vega-muted-2">Sem tarefas neste deal.</p>
      ) : (
        <div className="flex flex-col">
          {tasks.map((t) => {
            const done = t.state === 'Done' || t.state === 'Cancelled';
            const overdue = isOverdue(t, now);
            return (
              <div key={t.id} className="flex items-center gap-3 border-b border-vega-line-2 py-2.5 text-[12.5px]">
                <button
                  type="button"
                  onClick={() => toggle(t)}
                  aria-label={t.state === 'Done' ? 'Reabrir tarefa' : 'Concluir tarefa'}
                  className={`inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[3px] border-[1.5px] text-white ${
                    t.state === 'Done' ? 'border-vega-accent bg-vega-accent' : 'border-vega-line bg-transparent'
                  }`}
                >
                  {t.state === 'Done' && <Check size={10} strokeWidth={2.5} />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className={`font-medium ${done ? 'text-vega-muted line-through' : 'text-vega-ink'}`}>{t.title}</div>
                </div>
                <Tag tone={STATE_TONE[t.state]} size="sm">
                  {TASK_STATE_LABELS[t.state]}
                </Tag>
                <div className={`w-[90px] text-right font-mono text-[11px] ${overdue ? 'text-vega-danger' : 'text-vega-muted'}`}>
                  {t.dueDateUtc ? fmtDateShort(t.dueDateUtc) : '—'}
                </div>
                <Avatar name={userName(t.assigneeUserId)} colorKey={t.assigneeUserId} size={20} />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
